import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

function emptyMember() {
  return { name: '', email: '', password: '', role: 'collaborateur' };
}

export default function EquipePage() {
  const { user, refreshUser } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyMember());

  const [editing, setEditing] = useState(null); // membre en cours d'édition
  const [editForm, setEditForm] = useState({ name: '', role: 'collaborateur', password: '' });

  const [orgName, setOrgName] = useState('');
  const [editingOrg, setEditingOrg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/team');
      setOrg(r.data.org);
      setOrgName(r.data.org?.name || '');
      setMembers(r.data.members || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMember = async (e) => {
    e.preventDefault();
    try {
      await api.post('/team/members', form);
      setShowAdd(false);
      setForm(emptyMember());
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const openEdit = (m) => {
    setEditing(m);
    setEditForm({ name: m.name, role: m.role, password: '' });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: editForm.name, role: editForm.role };
      if (editForm.password) payload.password = editForm.password;
      await api.put(`/team/members/${editing.id}`, payload);
      setEditing(null);
      load();
      if (editing.id === user.id) refreshUser();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Supprimer le compte de ${m.name} ?\n\nSes clients, factures et prestations RESTENT dans le cabinet. Ses données personnelles (tâches perso, agenda perso…) seront supprimées.`)) return;
    try {
      await api.delete(`/team/members/${m.id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const saveOrgName = async () => {
    if (!orgName.trim()) return;
    try {
      await api.put('/team/org', { name: orgName.trim() });
      setEditingOrg(false);
      load();
      refreshUser();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  if (!isAdmin) {
    return (
      <div>
        <div className="page-header"><h1>Équipe</h1></div>
        <div className="empty-state">
          <div className="empty-emoji">🔒</div>
          <p>Seul l'administrateur du cabinet peut gérer l'équipe.</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            Cabinet : <strong>{org?.name || '—'}</strong>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Équipe</h1>
          <p>Gérez les accès de votre cabinet</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(emptyMember()); setShowAdd(true); }}>+ Ajouter un collaborateur</button>
      </div>

      {/* Nom du cabinet */}
      <div className="card" style={{ padding: 16, marginBottom: 18 }}>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>🏛️ Cabinet</div>
        {editingOrg ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input className="form-input" value={orgName} onChange={e => setOrgName(e.target.value)} style={{ maxWidth: 320 }} autoFocus />
            <button className="btn btn-primary btn-sm" onClick={saveOrgName}>Enregistrer</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setEditingOrg(false); setOrgName(org?.name || ''); }}>Annuler</button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: '1.15rem', fontWeight: 800 }}>{org?.name || '—'}</span>
            <button className="btn-ghost" onClick={() => setEditingOrg(true)} title="Renommer">✏️</button>
          </div>
        )}
      </div>

      {/* Membres */}
      {loading ? (
        <div className="empty-state"><p>Chargement…</p></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {members.map(m => (
            <div key={m.id} className="task-item">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700 }}>{m.name}</span>
                  <span className="badge" style={{ background: m.role === 'admin' ? '#eef2ff' : '#f1f5f9', color: m.role === 'admin' ? '#6366f1' : '#64748b' }}>
                    {m.role === 'admin' ? '👑 Admin' : '👤 Collaborateur'}
                  </span>
                  {m.is_me && <span className="badge" style={{ background: '#ecfdf5', color: '#10b981' }}>Moi</span>}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: 3 }}>✉️ {m.email}</div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button className="btn-ghost" onClick={() => openEdit(m)} title="Modifier">✏️</button>
                {!m.is_me && <button className="btn-ghost" onClick={() => removeMember(m)} title="Supprimer">🗑️</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 16, padding: 12, background: 'var(--primary-bg)', borderRadius: 10, fontSize: '0.82rem', color: 'var(--text-light)' }}>
        💡 Les collaborateurs voient <strong>les mêmes clients, dossiers, prestations et factures</strong> que toi, ainsi que les <strong>tâches et l'agenda de catégorie « travail »</strong>. Leur vie personnelle (repas, enfants, tâches perso…) reste privée.
      </div>

      {/* Modale ajout */}
      {showAdd && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>Ajouter un collaborateur</h2>
              <button type="button" className="btn-icon" onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <form onSubmit={addMember}>
              <div className="form-group">
                <label>Nom *</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required autoFocus />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Mot de passe provisoire * (min. 6 caractères)</label>
                <input className="form-input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>Communique-le à ton collaborateur ; il pourra le changer via « Mot de passe oublié ».</div>
              </div>
              <div className="form-group">
                <label>Rôle</label>
                <select className="form-input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  <option value="collaborateur">👤 Collaborateur</option>
                  <option value="admin">👑 Administrateur</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Créer le compte</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale édition */}
      {editing && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>Modifier {editing.name}</h2>
              <button type="button" className="btn-icon" onClick={() => setEditing(null)}>✕</button>
            </div>
            <form onSubmit={saveEdit}>
              <div className="form-group">
                <label>Nom</label>
                <input className="form-input" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input className="form-input" value={editing.email} disabled style={{ opacity: 0.7 }} />
              </div>
              <div className="form-group">
                <label>Rôle</label>
                <select className="form-input" value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })} disabled={editing.is_me}>
                  <option value="collaborateur">👤 Collaborateur</option>
                  <option value="admin">👑 Administrateur</option>
                </select>
                {editing.is_me && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>Vous ne pouvez pas modifier votre propre rôle.</div>}
              </div>
              <div className="form-group">
                <label>Nouveau mot de passe (laisser vide pour ne pas changer)</label>
                <input className="form-input" value={editForm.password} onChange={e => setEditForm({ ...editForm, password: e.target.value })} placeholder="••••••" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
